/** @jsx isolateComponent */
import { TooltipWrapper } from '../../display/TooltipWrapper';
import {
  Button,
  DefaultButtonColors,
  ButtonProps,
} from '../../navigation/Button';
import { Icon, IconName } from '../../display/Icon';
import { isolateComponent } from '@zapier/style-encapsulation';

import { TooltipPosition } from '../../display/Tooltip';

type Props = Omit<ButtonProps, 'children' | 'isPadded'> & {
  /**
   * Name of the `Icon` to render.
   */
  icon: IconName;
  /**
   * Size of the `Icon` that renders within the `Button`.
   * Typically unnecessary since the `size` prop will map
   * the desired `Icon` size in most circumstances.
   */
  iconSize?: number;
  /**
   * Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label?: string;
  /** Indicates whether to flip the `Tooltip` based on available space. */
  shouldCorrectTooltipPosition?: boolean;
  /**
   * Optional prop to associate the tooltip and the button that controls it.
   */
  tooltipId?: string;
  /**
   * Where to render the `Tooltip` relative to the button.
   */
  tooltipPosition?: TooltipPosition;
  /** Renders the IconButton in a pill shaped format. */
  isPill?: boolean;
};

/**
 * `IconButton`s are `Buttons` that render `Icon`s within them for
 * compact spaces. They accept any props that `Button` accepts, although
 * they'll always be `isSquare={true}`. Additionally, `IconButton` renders
 * a `Tooltip` which describes what the `Button` does, which is important
 * for accessibility.
 */
export function IconButton({
  icon,
  iconSize,
  label,
  shouldCorrectTooltipPosition,
  tooltipId,
  tooltipPosition,
  ...buttonProps
}: Props) {
  return (
    <TooltipWrapper
      content={label}
      position={tooltipPosition}
      shouldCorrectPosition={shouldCorrectTooltipPosition}
      showTooltipOnTouchDevices={false}
      tooltipId={tooltipId}
    >
      {({ childProps }) => (
        <Button
          ariaLabel={label}
          {...buttonProps}
          {...childProps}
          isSquare={true}
        >
          <Icon
            canAcceptPointerEvents={false}
            isBlock={true}
            name={icon}
            size={24}
          />
        </Button>
      )}
    </TooltipWrapper>
  );
}

IconButton.defaultProps = {
  color: 'greyAlt',
  colorTheme: DefaultButtonColors,
  size: 'small',
  tooltipPosition: 'south',
};
